import React, {useEffect} from "react";
import {useSearchParams} from "react-router-dom";
import './ListingsPage.scss';
import {SearchBar} from "./SearchBar";
import {ActiveFiltersBar} from "./ActiveFiltersBar";
import {Sidebar} from "./Sidebar";
import {MainContent} from "./MainContent";
import {SavedDealershipsPanel} from "./SavedDealershipsPanel";
import {isSameVehicleSeed, seedFiltersForVehicle, seedLocation} from "./utils";
import useDidMountEffect from "../../hooks/useDidMountEffect";
import {useDebounce} from "@uidotdev/usehooks";
import {useListingsStore} from "../../stores/listingsStore";
import {useSavedDealershipsStore} from "../../stores/savedDealershipsStore";
import {useShallow} from "zustand/react/shallow";
import {useThemeStore} from "../../stores/themeStore";


export default function ListingsPage() {
    const [searchParams, setSearchParams] = useSearchParams()
    const theme = useThemeStore((state) => state.theme)

    const {
        search,
        searchCars,
        fetchListings,
        filters,
        setFilters,
    } = useListingsStore(
        useShallow((state) => ({
            search: state.search,
            searchCars: state.searchCars,
            fetchListings: state.fetchListings,
            filters: state.filters,
            setFilters: state.setFilters,
        }))
    )

    const debouncedSearch = useDebounce(search, 300)
    const fetchSavedDealerships = useSavedDealershipsStore((state) => state.fetchSaved)

    useEffect(function fetchListingsOnMount(){
        // Load saved dealerships BEFORE the first listings fetch, so that single
        // request already carries the "My Dealers" dealer filter — otherwise we'd
        // make a throwaway first call (no dealer_id) and refetch once they load.
        //
        // When opened from a deal (DealPage "Find Inventory"), the vehicle arrives
        // as ?year=&make=&model=&trim= params + the dealer's ?zipcode= location.
        (async () => {
            await fetchSavedDealerships()

            // Model-level search: seed make/model (ignore any ?trim=). Year is a
            // multi-select, so it lives in `years` (a comma-joined ?year=), not in
            // searchTags.
            const searchTags = {}
            for (const key of ['make', 'model']) {
                const value = searchParams.get(key)
                if (value) searchTags[key] = value
            }

            const yearParam = searchParams.get('year')
            const years = yearParam ? yearParam.split(',').filter(Boolean) : []
            const zipcode = searchParams.get('zipcode')
            const carParam = searchParams.get('car')
            // New/Used preselect, carried by the quote modal's "Open in new tab".
            const inventoryType = searchParams.get('inventory_type')

            if (Object.keys(searchTags).length || years.length || zipcode || carParam || inventoryType) {
                // `car` = exact-trim BaseCar pk (may be absent for name-based seeds).
                const vehicle = {...searchTags, years, car: carParam ? Number(carParam) : null}
                // A DIFFERENT vehicle is a fresh search, so the payload also resets
                // the sidebar facets left over from the previous one (they belong to
                // the vehicle being replaced) — see seedFiltersForVehicle. Params that
                // merely restore the search already in the store are a revisit (the
                // URL mirrors the vehicle, so Back lands here) and keep them.
                // ?inventory_type rides on the fresh-seed branch only: on a revisit
                // New/Used is a sidebar facet the user owns, and the param is the
                // stale one they may already have changed away from.
                const newFilters = isSameVehicleSeed(vehicle, filters)
                    ? {searchTags, years, car: vehicle.car}
                    : seedFiltersForVehicle({...vehicle, inventory_type: inventoryType})
                if (zipcode) {
                    newFilters.location = seedLocation(filters.location, zipcode)
                }
                setFilters(newFilters, true)
            } else {
                fetchListings(true)
            }
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Keep the URL params in sync with the search-bar pills — so removing a pill
    // (or picking a suggestion) updates the URL to match. Runs after mount, so
    // the initial seed above wins on first load. Other params (e.g. zipcode) are
    // preserved.
    useDidMountEffect(function syncUrlWithSearchTags(){
        const params = new URLSearchParams(searchParams)
        for (const key of ['make', 'model']) {
            const value = filters.searchTags[key]
            if (value) params.set(key, value)
            else params.delete(key)
        }
        // Year is a multi-select — mirror it as a comma-joined ?year=.
        const years = filters.years || []
        if (years.length) params.set('year', years.join(','))
        else params.delete('year')
        params.delete('trim')  // model-level search — never mirror a trim to the URL
        // Mirror the exact-trim `car` pk into the URL alongside the tags.
        if (filters.car) params.set('car', String(filters.car))
        else params.delete('car')
        setSearchParams(params, {replace: true})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.searchTags, filters.car, filters.years])

    const debouncedfilters = useDebounce(filters, 500)

    useDidMountEffect(function fetchListingsOnFilterChange(){
        // fetchListings()
    }, [debouncedfilters])

    // useDidMountEffect(function searchBarEffect(){
    //     const searchData = structuredClone(filters.searchTags)
    //     if (debouncedSearch) {
    //         searchData['input'] = search
    //     }
    //
    //     searchCars(searchData)
    // }, [debouncedSearch])

    return (
        <div id={"ListingsPage"} data-theme={theme}>
            <SearchBar />
            <ActiveFiltersBar />
            <div className="layout">
                <Sidebar />
                <MainContent />
                <SavedDealershipsPanel />
            </div>
        </div>
    );
}